import { ReactNode, MouseEvent } from 'react';
import styles from './CardList.module.scss';

/*
 * הפרימיטיבות של כרטיס הרשימה, בתרגום ישיר מ-hatzdaka-nedarim
 * (js/common/card-list.js ו-js/components/record-card.js). שמות
 * הפונקציות שם — icon / body / title / meta / amount / side —
 * נשמרים כאן כשמות רכיבים, כדי ששתי המימושים ייקראו זה מול זה.
 *
 * ⚠️ אלה רכיבי **תצוגה בלבד**. אין כאן לוגיקה, אין קריאות API ואין
 * החלטות על מה מוצג: מי שמרכיב את הכרטיס הוא העמוד, בדיוק כמו ש-
 * TAB_CONFIG[x].card.build עושה שם. הכלל שעומד מאחורי המסך הזה הוא
 * שאף פעולה לא נעלמת במעבר לנייד, ולכן שום רכיב כאן לא מסנן ולא
 * מקצר — אם עמוד מחליט להסתיר משהו, זו החלטה שלו ומתועדת אצלו.
 */

/* ------------------------------------------------------------
 *  המיכל
 * ------------------------------------------------------------ */

interface CardListProps {
  /**
   * flat  — שורה שטוחה ברוחב מלא, מופרדת בקו דק (תנועות).
   * boxed — מיכל אפור ובתוכו כרטיסים לבנים (בקשות תמיכה).
   *
   * ההבחנה מגיעה מהעיצוב המקורי ואינה קישוט: שני המבנים אומרים
   * דברים שונים על התוכן, ולכן לוח לא מחליף ביניהם לפי נוחות.
   */
  layout: 'flat' | 'boxed';
  children: ReactNode;
  className?: string;
}

export const CardList = ({ layout, children, className }: CardListProps) => (
  <div className={`${styles.cardList} ${layout === 'boxed' ? styles.boxed : styles.flat} ${className || ''}`}>
    {/* עוטף פנימי — רק הוא הופך לרשת שתי עמודות בטאבלט, כדי שריפוד
        המיכל האפור יישאר מחוץ לרשת ולא ייספר כתא. */}
    <div className={styles.cards}>{children}</div>
  </div>
);

/* ------------------------------------------------------------
 *  הכרטיס
 * ------------------------------------------------------------ */

interface ListCardProps {
  onClick?: () => void;
  /** הכרטיס שהמגירה פתוחה עליו — מקבל את אותו רקע כמו שורה פעילה בטבלה. */
  isActive?: boolean;
  children: ReactNode;
}

/*
 * ⚠️ div ולא button, למרות שהוא נלחץ.
 *
 * בתוך הכרטיס יושבים כפתורי פעולה משלו (הורדת קבלה, פתיחת פנייה,
 * תפריט) ותיבת סימון. כפתור בתוך כפתור אינו HTML חוקי, והדפדפן
 * מתקן אותו בכך שהוא **מוציא את הכפתור הפנימי מהכרטיס** — כלומר
 * הפעולות היו מאבדות את מיקומן. הנגישות נשמרת ב-role ו-tabIndex,
 * והמקלדת מטופלת במפורש.
 */
export const ListCard = ({ onClick, isActive, children }: ListCardProps) => (
  <div
    className={`${styles.listCard} ${isActive ? styles.active : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    // A card opens its row's blade, and the same tap then reaches the document, where the blade
    // reads any click outside it as "close" - unless it comes from something marked as opening it.
    // Without this, a card on a phone opened the order's details and shut them in one tap.
    data-opens-blade={onClick ? '' : undefined}
    onKeyDown={(event) => {
      if (!onClick) return;
      // רק כשהמיקוד על הכרטיס עצמו. בלי זה Enter על כפתור פעולה
      // שבתוכו היה מפעיל גם את הפעולה וגם את פתיחת המגירה.
      if (event.target !== event.currentTarget) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onClick();
    }}
  >
    {children}
  </div>
);

/* ------------------------------------------------------------
 *  אזורי הכרטיס
 * ------------------------------------------------------------ */

/** עיגול האייקון בתחילת השורה. */
export const CardIcon = ({ children }: { children: ReactNode }) => (
  <div className={styles.icon} aria-hidden="true">
    {children}
  </div>
);

export const CardBody = ({ children }: { children: ReactNode }) => <div className={styles.body}>{children}</div>;

export const CardTitle = ({ children, strong }: { children: ReactNode; strong?: boolean }) => (
  <div className={`${styles.title} ${strong ? styles.titleStrong : ''}`}>{children}</div>
);

/*
 * שורת המטא. מקבלת מערך ומשמיטה ערכים ריקים בעצמה — בדיוק כמו
 * CardList.meta במקור — כדי שהעמוד יוכל לכתוב שדה מותנה כ-null
 * ולא לבנות מערך בתנאים.
 */
export const CardMeta = ({
  items,
  ltr,
  separator = ' | ',
}: {
  items: (string | null | undefined | false)[];
  ltr?: boolean;
  /** HAT-1238: הוראות קבע בפיגמה משתמשות ב-· ולא ב-| של נדרים. */
  separator?: string;
}) => {
  const shown = items.filter((item): item is string => Boolean(item));
  if (shown.length === 0) return null;

  /* ברירת המחדל היא "|", כמו ב-CardList.meta במקור. */
  return <div className={`${styles.meta} ${ltr ? styles.metaLtr : ''}`}>{shown.join(separator)}</div>;
};

/**
 * הסכום. `positive` צובע ירוק — "כסף שנכנס לקרן".
 *
 * ⚠️ סכום של בקשת תמיכה הוא סכום **מבוקש**, לא תנועה שקרתה, ולכן
 * הוא נמסר כ-positive={false} גם כשהוא חיובי. לצבוע אותו בירוק
 * היה קורא לו הכנסה.
 */
export const CardAmount = ({
  children,
  positive,
  muted,
}: {
  children: ReactNode;
  positive?: boolean;
  /** HAT-1238: סכום אפור כשיש חריג (נכשל / לא פעילה), כמו בפיגמה. */
  muted?: boolean;
}) => (
  <div
    className={`${styles.amount} ${muted ? styles.amountMuted : positive ? styles.amountPositive : styles.amountNegative}`}
  >
    {children}
  </div>
);

/** העמודה השמאלית: סכום מעל סטטוס. */
export const CardSide = ({ children }: { children: ReactNode }) => <div className={styles.side}>{children}</div>;

export const CardChips = ({ children }: { children: ReactNode }) => <div className={styles.chips}>{children}</div>;

export const CardChip = ({ children, muted }: { children: ReactNode; muted?: boolean }) => (
  <span className={`${styles.chip} ${muted ? styles.chipMore : ''}`}>{children}</span>
);

/* ------------------------------------------------------------
 *  סטטוס
 * ------------------------------------------------------------ */

export const CardStatusLine = ({ color, children }: { color?: string; children: ReactNode }) => (
  <div className={styles.statusLine}>
    <span className={styles.statusDot} style={color ? { background: color } : undefined} />
    <span>{children}</span>
  </div>
);

export const CardStatusPill = ({ children }: { children: ReactNode }) => (
  <span className={styles.statusPill}>{children}</span>
);

/* ------------------------------------------------------------
 *  פעולות השורה
 * ------------------------------------------------------------ */

export const CardRowActions = ({ children }: { children: ReactNode }) => (
  <div className={styles.rowActions}>{children}</div>
);

interface CardIconButtonProps {
  /** מוצג כ-tooltip וכשם הנגיש — שני השימושים, כי אין כאן טקסט. */
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

/*
 * כפתור פעולה בשורה.
 *
 * ⚠️ stopPropagation הוא הכרחי: הכרטיס כולו פותח את המגירה, ובלעדיו
 * כל הקשה על "הורדת קבלה" הייתה גם מורידה את הקבלה וגם פותחת את
 * המגירה מעליה.
 */
export const CardIconButton = ({ title, onClick, disabled, children }: CardIconButtonProps) => (
  <button
    type="button"
    className={styles.iconButton}
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={(event: MouseEvent) => {
      event.stopPropagation();
      onClick();
    }}
  >
    {children}
  </button>
);

/*
 * עוטף לפעולה שמגיעה כרכיב מוכן (למשל OpenInquiryButton).
 *
 * ⚠️ בלי מחלקת iconButton, ובכוונה: הרכיב הפנימי כבר מרנדר כפתור
 * מעוצב משלו, ועיגול נוסף מסביבו היה מצייר עיגול בתוך עיגול. מה
 * שהעוטף הזה נותן הוא רק עצירת ההתפשטות — בלעדיה הקשה על "פתח
 * פנייה" הייתה גם פותחת פנייה וגם פותחת את המגירה מעליה.
 */
export const CardAction = ({ children }: { children: ReactNode }) => (
  <span
    className={styles.action}
    onClick={(event: MouseEvent) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
    role="presentation"
  >
    {children}
  </span>
);

/** תיבת הסימון של הבחירה המרובה, בתחילת השורה. */
export const CardSelect = ({ children }: { children: ReactNode }) => (
  <div
    className={styles.select}
    onClick={(event: MouseEvent) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
    role="presentation"
  >
    {children}
  </div>
);

/*
 * שומר את המקום של כפתור שאינו מוצג בשורה הזאת.
 *
 * ⚠️ בלי זה הכפתורים זזים משורה לשורה: תנועה בלי קבלה אין לה תפריט,
 * ולכן אייקון הפנייה שלה נדחק שמאלה לעומת השורה שמעליה — ועמודת
 * הפעולות נראית שבורה. זה בדיוק מה ש-TransactionRowActions עושה
 * בטבלה ("Kinds that never have a receipt keep the space").
 */
export const CardIconButtonSlot = () => (
  <span className={styles.iconButton} style={{ visibility: 'hidden' }} aria-hidden="true" />
);
